import React, { useState, useEffect, useMemo } from 'react';
import { apiCall } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface ProvaDetalhe {
  prova: string;
  pontos: number;
}

interface RankingEntry {
  rank: number;
  binomio: string;
  nomeCao: string;
  nomePessoa: string;
  nomeClube: string;
  pontosValidos: number;
  pontosFora: number;
  pontosObtidos: number;
  provas?: ProvaDetalhe[];
}

interface RankingCategoria {
  nome: string;
  entries: RankingEntry[];
}

interface RankingData {
  rankings: Record<string, RankingCategoria>;
  previousRankings?: Record<string, RankingCategoria>;
  categorias: Record<string, string>;
  debug?: {
    sheetsFound: string[];
    categoriesMatched: string[];
  };
}

function entryKey(e: RankingEntry): string {
  return `${(e.nomeCao ?? '').trim()}|${(e.nomePessoa ?? '').trim()}`;
}

const CATEGORIA_ORDER = [
  'G3_L', 'G3_I', 'G3_M', 'G3_S',
  'G2_L', 'G2_I', 'G2_M', 'G2_S',
  'INF_G2_I'
];

const RankingPage: React.FC = () => {
  const [data, setData] = useState<RankingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [filterCao, setFilterCao] = useState('');
  const [filterPessoa, setFilterPessoa] = useState('');
  const [filterClube, setFilterClube] = useState('');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<RankingEntry | null>(null);

  useEffect(() => {
    loadRanking();
  }, []);

  const loadRanking = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiCall('/ranking', { method: 'GET' });
      setData(res);
      // Selecionar a primeira categoria disponível
      if (res.rankings) {
        const availableCategories = CATEGORIA_ORDER.filter(key => res.rankings[key]);
        if (availableCategories.length > 0 && !selectedCategoria) {
          setSelectedCategoria(availableCategories[0]);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar ranking.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Categorias disponíveis (ordenadas)
  const availableCategories = useMemo(() => {
    if (!data?.rankings) return [];
    return CATEGORIA_ORDER.filter(key => data.rankings[key]);
  }, [data]);

  // Entries da categoria selecionada, ordenadas por rank e depois por pontosObtidos (desc)
  const currentEntries = useMemo(() => {
    if (!data?.rankings || !selectedCategoria) return [];
    const entries = data.rankings[selectedCategoria]?.entries || [];
    return [...entries].sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return (b.pontosObtidos ?? 0) - (a.pontosObtidos ?? 0);
    });
  }, [data, selectedCategoria]);

  // Mapa: chave do binómio -> rank na última leitura com alterações (previousRankings)
  const previousRankByKey = useMemo(() => {
    const map: Record<string, number> = {};
    if (!data?.previousRankings || !selectedCategoria) return map;
    const prevEntries = data.previousRankings[selectedCategoria]?.entries || [];
    prevEntries.forEach((e) => {
      map[entryKey(e)] = e.rank;
    });
    return map;
  }, [data?.previousRankings, selectedCategoria]);

  // 'up' = subiu, 'down' = desceu, null = sem alteração ou novo no ranking
  const getRankChange = (entry: RankingEntry): 'up' | 'down' | null => {
    const prev = previousRankByKey[entryKey(entry)];
    if (prev == null) return null;
    if (entry.rank < prev) return 'up';
    if (entry.rank > prev) return 'down';
    return null;
  };

  // Lista única de cães para filtro
  const caosDisponiveis = useMemo(() => {
    const set = new Set<string>();
    currentEntries.forEach(e => {
      if (e.nomeCao?.trim()) set.add(e.nomeCao.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-PT'));
  }, [currentEntries]);

  // Lista única de pessoas para filtro
  const pessoasDisponiveis = useMemo(() => {
    const set = new Set<string>();
    currentEntries.forEach(e => {
      if (e.nomePessoa?.trim()) set.add(e.nomePessoa.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-PT'));
  }, [currentEntries]);

  // Lista única de clubes para filtro
  const clubesDisponiveis = useMemo(() => {
    const set = new Set<string>();
    currentEntries.forEach(e => {
      if (e.nomeClube?.trim()) set.add(e.nomeClube.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-PT'));
  }, [currentEntries]);

  // Aplicar filtros
  const filteredEntries = useMemo(() => {
    let list = [...currentEntries];
    
    if (filterCao) {
      list = list.filter(e => e.nomeCao?.trim() === filterCao);
    }
    if (filterPessoa) {
      list = list.filter(e => e.nomePessoa?.trim() === filterPessoa);
    }
    if (filterClube) {
      list = list.filter(e => e.nomeClube?.trim() === filterClube);
    }
    
    return list;
  }, [currentEntries, filterCao, filterPessoa, filterClube]);

  const hasActiveFilters = !!(filterCao || filterPessoa || filterClube);

  const clearFilters = () => {
    setFilterCao('');
    setFilterPessoa('');
    setFilterClube('');
  };

  const closeFiltersMobile = () => setShowFiltersMobile(false);

  const getCategoriaLabel = (key: string) => {
    return data?.categorias?.[key] || key;
  };

  // Formatar pontos com separador de milhares
  const formatPoints = (points: number) => {
    return points.toLocaleString('pt-PT');
  };

  // Badge por posição: 1º–3º com stroke (contorno), resto neutro
  const getRankBadgeClass = (rank: number) => {
    switch (rank) {
      case 1: return 'badge-rank-1';
      case 2: return 'badge-rank-2';
      case 3: return 'badge-rank-3';
      default: return 'bg-light text-dark border';
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <LoadingSpinner />
          <p className="text-muted mt-2">A carregar ranking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning shadow-sm">
          <h5 className="alert-heading">
            <i className="fas fa-trophy me-2"></i>
            Ranking CPC Agility
          </h5>
          <p className="mb-0">{error}</p>
        </div>
      </div>
    );
  }

  if (!data || availableCategories.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-info shadow-sm">
          <h5 className="alert-heading">
            <i className="fas fa-trophy me-2"></i>
            Ranking CPC Agility
          </h5>
          <p className="mb-0">Nenhuma categoria de ranking disponível.</p>
          {data?.debug && (
            <div className="mt-3 small">
              <p className="mb-1"><strong>Debug info:</strong></p>
              <p className="mb-1">Folhas encontradas no Excel: {data.debug.sheetsFound?.length > 0 ? data.debug.sheetsFound.join(', ') : 'Nenhuma'}</p>
              <p className="mb-0">Categorias reconhecidas: {data.debug.categoriesMatched?.length > 0 ? data.debug.categoriesMatched.join(', ') : 'Nenhuma'}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-3 py-md-4" style={{ paddingBottom: '3rem' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 mb-md-4">
          <h4 className="mb-0" style={{ color: '#a2449c' }}>
            <i className="fas fa-trophy me-2"></i>
            Ranking CPC Agility
          </h4>
        </div>

        {/* Tabs de Categorias */}
        <div className="card shadow-sm mb-4">
          <div className="card-body p-2 p-md-3">
            <div className="d-flex flex-wrap gap-2">
              {availableCategories.map(key => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm ${selectedCategoria === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={selectedCategoria === key ? { backgroundColor: '#a2449c', borderColor: '#a2449c' } : {}}
                  onClick={() => {
                    setSelectedCategoria(key);
                    clearFilters();
                  }}
                >
                  {key.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Nome da Categoria */}
        <div className="mb-3">
          <h5 className="text-muted mb-0">
            <i className="fas fa-medal me-2"></i>
            {getCategoriaLabel(selectedCategoria)}
          </h5>
        </div>

        {/* Filtros */}
        <div className="card shadow-sm mb-4">
          <div
            className="card-header py-2 d-md-none"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowFiltersMobile(v => !v)}
            role="button"
            aria-expanded={showFiltersMobile}
            aria-label={showFiltersMobile ? 'Ocultar filtros' : 'Mostrar filtros'}
          >
            <h6 className="mb-0 text-muted d-flex align-items-center">
              <i className="fas fa-filter me-2"></i>
              Filtros
              {hasActiveFilters && (
                <i
                  className="fas fa-exclamation-circle ms-2"
                  style={{ color: '#e65100' }}
                  title="Filtros aplicados"
                  aria-hidden="true"
                ></i>
              )}
              <i className={`fas fa-chevron-${showFiltersMobile ? 'up' : 'down'} ms-auto mt-1`}></i>
            </h6>
          </div>
          <div className={`card-body p-3 ${showFiltersMobile ? '' : 'd-none'} d-md-block`}>
            <h6 className="text-muted mb-3 d-none d-md-flex align-items-center">
              <i className="fas fa-filter me-2"></i>
              Filtros
              {hasActiveFilters && (
                <i className="fas fa-exclamation-circle ms-2" style={{ color: '#e65100' }} title="Filtros aplicados"></i>
              )}
            </h6>
            <div className="row g-2 g-md-3">
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small text-muted mb-1">Cão</label>
                <select
                  className="form-select form-select-sm"
                  value={filterCao}
                  onChange={(e) => { setFilterCao(e.target.value); closeFiltersMobile(); }}
                >
                  <option value="">Todos os cães</option>
                  {caosDisponiveis.map(nome => (
                    <option key={nome} value={nome}>{nome}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small text-muted mb-1">Handler</label>
                <select
                  className="form-select form-select-sm"
                  value={filterPessoa}
                  onChange={(e) => { setFilterPessoa(e.target.value); closeFiltersMobile(); }}
                >
                  <option value="">Todos os handlers</option>
                  {pessoasDisponiveis.map(nome => (
                    <option key={nome} value={nome}>{nome}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small text-muted mb-1">Clube</label>
                <select
                  className="form-select form-select-sm"
                  value={filterClube}
                  onChange={(e) => { setFilterClube(e.target.value); closeFiltersMobile(); }}
                >
                  <option value="">Todos os clubes</option>
                  {clubesDisponiveis.map(nome => (
                    <option key={nome} value={nome}>{nome}</option>
                  ))}
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearFilters}
                >
                  <i className="fas fa-times me-1"></i>
                  Limpar filtros
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabela de Ranking */}
        {filteredEntries.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <i className="fas fa-search fa-3x mb-3" style={{ color: '#a2449c' }}></i>
              <p className="text-muted mb-0">
                {currentEntries.length === 0
                  ? 'Nenhum resultado encontrado nesta categoria.'
                  : 'Nenhum resultado corresponde aos filtros selecionados.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Vista Mobile - Cards */}
            <div className="d-md-none">
              {filteredEntries.map((entry, idx) => (
                <div 
                  key={`${entry.rank}-${idx}`} 
                  className="card shadow-sm mb-3"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedEntry(entry)}
                >
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="rank-cell-inner" style={{ width: '4.5rem', minWidth: '4.5rem' }}>
                          <span className={`badge rank-badge-fixed ${getRankBadgeClass(entry.rank)} fs-6`}>
                            {entry.rank}º
                          </span>
                          {getRankChange(entry) === 'up' && (
                            <i className="fas fa-arrow-up" style={{ color: 'rgba(25,135,84,0.75)', fontSize: '0.65rem' }} title="Subiu no ranking" aria-hidden />
                          )}
                          {getRankChange(entry) === 'down' && (
                            <i className="fas fa-arrow-down" style={{ color: 'rgba(220,53,69,0.75)', fontSize: '0.65rem' }} title="Desceu no ranking" aria-hidden />
                          )}
                        </span>
                        <div>
                          <div className="fw-bold" style={{ color: '#a2449c' }}>{entry.nomeCao}</div>
                          <div className="small text-muted">{entry.nomePessoa}</div>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold fs-5" style={{ color: '#a2449c' }}>
                          {formatPoints(entry.pontosObtidos)}
                        </div>
                        <div className="small text-muted">pontos</div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="small text-muted">
                        <i className="fas fa-users me-1"></i>
                        {entry.nomeClube}
                      </div>
                      {entry.provas && entry.provas.length > 0 && (
                        <div className="small text-muted">
                          <i className="fas fa-chevron-right"></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Vista Desktop - Tabela */}
            <div className="d-none d-md-block">
              <div className="card shadow-sm">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th className="rank-cell text-start">#</th>
                        <th>Cão</th>
                        <th>Handler</th>
                        <th>Clube</th>
                        <th className="text-end" style={{ width: '120px' }}>Pontos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEntries.map((entry, idx) => (
                        <tr 
                          key={`${entry.rank}-${idx}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedEntry(entry)}
                          className="ranking-row"
                        >
                          <td className="rank-cell align-middle">
                            <span className="rank-cell-inner">
                              <span className={`badge rank-badge-fixed ${getRankBadgeClass(entry.rank)}`}>
                                {entry.rank}º
                              </span>
                              {getRankChange(entry) === 'up' && (
                                <i className="fas fa-arrow-up" style={{ color: 'rgba(25,135,84,0.75)', fontSize: '0.65rem' }} title="Subiu no ranking" aria-hidden />
                              )}
                              {getRankChange(entry) === 'down' && (
                                <i className="fas fa-arrow-down" style={{ color: 'rgba(220,53,69,0.75)', fontSize: '0.65rem' }} title="Desceu no ranking" aria-hidden />
                              )}
                            </span>
                          </td>
                          <td className="align-middle">
                            <span className="fw-bold" style={{ color: '#a2449c' }}>{entry.nomeCao}</span>
                          </td>
                          <td className="align-middle">{entry.nomePessoa}</td>
                          <td className="align-middle">
                            <span className="text-muted">{entry.nomeClube}</span>
                          </td>
                          <td className="text-end align-middle">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <span className="fw-bold">{formatPoints(entry.pontosObtidos)}</span>
                              {entry.provas && entry.provas.length > 0 && (
                                <i className="fas fa-chevron-right text-muted small"></i>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Resumo */}
            <div className="mt-3 text-muted small">
              {filteredEntries.length} {filteredEntries.length === 1 ? 'resultado' : 'resultados'}
              {hasActiveFilters && ` (de ${currentEntries.length} total)`}
            </div>
          </>
        )}
      </div>

      {/* Modal de Detalhes */}
      {selectedEntry && (
        <div
          className="modal fade show d-block ranking-detail-modal"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setSelectedEntry(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#a2449c', color: 'white' }}>
                <div className="d-flex align-items-center gap-3">
                  <span className={`badge ${getRankBadgeClass(selectedEntry.rank)} fs-5`}>
                    {selectedEntry.rank}º
                  </span>
                  <div>
                    <h5 className="modal-title mb-0">{selectedEntry.nomeCao}</h5>
                    <small className="opacity-75">{selectedEntry.nomePessoa}</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedEntry(null)}
                  aria-label="Fechar"
                ></button>
              </div>
              <div className="modal-body">
                {/* Resumo */}
                <div className="row text-center mb-4">
                  <div className="col-4">
                    <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="fs-4 fw-bold" style={{ color: '#28a745' }}>
                        {formatPoints(selectedEntry.pontosValidos)}
                      </div>
                      <small className="text-muted">Pontos Válidos</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="fs-4 fw-bold" style={{ color: '#dc3545' }}>
                        {formatPoints(selectedEntry.pontosFora)}
                      </div>
                      <small className="text-muted">Pontos Fora</small>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="fs-4 fw-bold" style={{ color: '#a2449c' }}>
                        {formatPoints(selectedEntry.pontosObtidos)}
                      </div>
                      <small className="text-muted">Total Obtido</small>
                    </div>
                  </div>
                </div>

                {/* Info do Clube */}
                <div className="mb-4 text-center">
                  <i className="fas fa-users me-2 text-muted"></i>
                  <span className="text-muted">{selectedEntry.nomeClube}</span>
                </div>

                {/* Pontos por Prova */}
                {selectedEntry.provas && selectedEntry.provas.length > 0 ? (
                  <>
                    <h6 className="mb-3">
                      <i className="fas fa-list-ol me-2" style={{ color: '#a2449c' }}></i>
                      Pontos por Prova
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-striped table-sm">
                        <thead>
                          <tr>
                            <th>Prova</th>
                            <th className="text-end">Pontos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedEntry.provas.map((prova, idx) => (
                            <tr key={idx}>
                              <td>{prova.prova}</td>
                              <td className="text-end fw-bold">
                                {prova.pontos > 0 ? (
                                  <span className="text-success">{formatPoints(prova.pontos)}</span>
                                ) : (
                                  <span className="text-muted">0</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="fas fa-info-circle me-2"></i>
                    Sem detalhes de provas disponíveis
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedEntry(null)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer
        className="text-center py-2 border-top bg-light"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          fontSize: '0.75rem',
          color: '#6c757d',
          zIndex: 1020,
        }}
      >
        Esta informação é da total responsabilidade do CPC Agility
      </footer>
    </>
  );
};

export default RankingPage;
